import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { adminApi } from '../../../api/adminApi';
import type { StudentAnalysisRow } from '../../../types/assignmentQuality';
import { configLabel } from '../../../types/assignmentQuality';
import { analysisDetailPath } from './assignmentRoutes';
import { AnswerCell, AssessmentQualityCell, CorrectnessBadge, HealthCell, ScoreCell } from './qualityCells';
import { sourceColumn, sourceGroupRow } from './columnSources';
import './AssignmentQuality.css';

const PAGE_SIZE = 25;

const WINDOWS = [
  { value: '50', label: '50 most recent' },
  { value: '100', label: '100 most recent' },
  { value: '200', label: '200 most recent' },
];

/** The assignment filter's value for work that came from no assignment. Empty
 *  string already means "any", so absence needs a name of its own. */
const NO_ASSIGNMENT = '__practice__';

/** Sortable columns → the value each sorts on. Real values, never rendered
 *  text: an undefined score must sort as absent, not as the string "—". */
const SORT_VALUES: Record<string, (r: StudentAnalysisRow) => string | number> = {
  submitted: r => Date.parse(r.submittedAt) || 0,
  assignment: r => (r.questionSetTitle ?? '').toLowerCase(),
  question: r => (r.questionText ?? r.questionId).toLowerCase(),
  correctAnswer: r => (r.correctAnswer ?? '').toLowerCase(),
  llmRead: r => (r.llmStudentAnswer ?? '').toLowerCase(),
  tutorVerdict: r => r.correctness ?? '',
  studentAccuracy: r => r.correctnessScore ?? -1,
  studentApproach: r => r.approachScore ?? -1,
  assessmentQuality: r => r.assessmentQuality?.verdict ?? '',
  config: r => `${r.config ?? ''}|${r.model ?? ''}`,
  health: r => r.qualityChecks.length + (r.status === 'failed' ? 100 : 0) + (r.thumbsDown ? 10 : 0),
};

/**
 * S4 — every solution analysis for one student, newest first.
 *
 * Deliberately NOT scoped to the assignment you arrived from: you come here
 * because something looked wrong there, and the next question is whether this
 * student's analyses look wrong generally (design §1).
 *
 * The server window IS the dataset: `window` most-recent analyses are fetched
 * from `offset`, then searched, filtered, sorted and paged in memory. Sorting a
 * 25-row server page would otherwise reorder those 25 and present the result as
 * "their worst analyses".
 */
export default function StudentAnalyses() {
  const { studentId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  // Arriving from an assignment pre-selects it; it is a filter value, not the
  // page's identity, so it lives alongside the others and can be cleared.
  const arrivedFrom = params.get('questionSetId') ?? '';

  const [filters, setFilters] = useState<Record<string, string>>({
    window: '100', assignment: arrivedFrom, subject: '', verdict: '', flagged: '', search: '',
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'submitted', sortOrder: 'desc' });
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);

  const windowSize = Number(filters.window) || 100;

  // The fetch is NOT narrowed by the assignment filter: the whole point of the
  // page is to see this student's other work next to it, so narrowing happens
  // in memory over the same window.
  const { data, isLoading, error } = useQuery({
    queryKey: ['studentAnalyses', studentId, windowSize, offset],
    queryFn: () => adminApi.listStudentAnalyses(studentId, { limit: windowSize, offset }),
    enabled: !!studentId,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  // Options come from the rows on screen — an assignment or verdict nobody
  // produced should not be offered as a filter that returns nothing.
  const filterDefs: FilterDef[] = useMemo(() => {
    const assignments = new Map<string, string>();
    let sawPractice = false;
    for (const r of rows) {
      if (r.questionSetId) assignments.set(r.questionSetId, r.questionSetTitle || r.questionSetId);
      else sawPractice = true;
    }
    return [
      { key: 'window', placeholder: 'Window', options: WINDOWS, required: true },
      {
        key: 'assignment',
        placeholder: 'Any assignment',
        options: [
          ...[...assignments.entries()]
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ value, label })),
          ...(sawPractice ? [{ value: NO_ASSIGNMENT, label: 'Practice (no assignment)' }] : []),
        ],
      },
      {
        key: 'subject',
        placeholder: 'Any subject',
        options: [...new Set(rows.map(r => r.subject).filter((s): s is string => !!s))]
          .sort().map(s => ({ value: s, label: s })),
      },
      {
        key: 'verdict',
        placeholder: 'Any app verdict',
        options: [...new Set(rows.map(r => r.correctness).filter((v): v is string => !!v))]
          .sort().map(v => ({ value: v, label: v.replace(/_/g, ' ') })),
      },
      {
        key: 'flagged',
        placeholder: 'Any health',
        options: [
          { value: 'flagged', label: 'Flagged only' },
          { value: 'clean', label: 'Clean only' },
        ],
      },
    ];
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = (filters.search ?? '').trim().toLowerCase();
    return rows.filter(r => {
      if (filters.assignment === NO_ASSIGNMENT && r.questionSetId) return false;
      if (filters.assignment && filters.assignment !== NO_ASSIGNMENT && r.questionSetId !== filters.assignment) return false;
      if (filters.subject && r.subject !== filters.subject) return false;
      if (filters.verdict && r.correctness !== filters.verdict) return false;
      const flagged = r.qualityChecks.length > 0 || r.status === 'failed' || r.thumbsDown;
      if (filters.flagged === 'flagged' && !flagged) return false;
      if (filters.flagged === 'clean' && flagged) return false;
      if (!needle) return true;
      return (r.questionText ?? '').toLowerCase().includes(needle)
        || (r.correctAnswer ?? '').toLowerCase().includes(needle)
        || (r.llmStudentAnswer ?? '').toLowerCase().includes(needle)
        || (r.questionSetTitle ?? '').toLowerCase().includes(needle)
        || (r.answer.text ?? '').toLowerCase().includes(needle);
    });
  }, [rows, filters]);

  const sorted = useMemo(() => {
    const valueOf = SORT_VALUES[sort.sortBy];
    if (!valueOf) return filtered;
    const factor = sort.sortOrder === 'asc' ? 1 : -1;
    // Copy: react-query's cached array must not be reordered in place.
    return [...filtered].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === bv) return 0;
      return (typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))) * factor;
    });
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // A filter change can shrink the list under the current page; clamp rather
  // than strand the user on a page they never navigated to.
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [sorted, safePage],
  );

  const columns: ColumnDef<StudentAnalysisRow>[] = useMemo(() => [
    sourceColumn<StudentAnalysisRow>('submitted', row => (
      <Link className="aq-link" to={analysisDetailPath(row.submissionId)}>
        {format(new Date(row.submittedAt), 'MMM d, HH:mm')}
      </Link>
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('assignment', row => (
      row.questionSetTitle
        ? <span>{row.questionSetTitle}</span>
        : <span className="aq-muted">practice</span>
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('question', row => (
      <div className="aq-qcell">
        <LatexText text={row.questionText || row.questionId} truncate={160} />
        <div className="aq-muted">{row.subject}{row.questionType ? ` · ${row.questionType}` : ''}</div>
      </div>
    ), { sortable: true, mobile: 'title' }),
    sourceColumn<StudentAnalysisRow>('correctAnswer', row => (
      row.correctAnswer ? <LatexText text={row.correctAnswer} /> : <span className="aq-muted">—</span>
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('studentAnswer', row => <AnswerCell answer={row.answer} />),
    sourceColumn<StudentAnalysisRow>('llmRead', row => (
      row.llmStudentAnswer ? <LatexText text={row.llmStudentAnswer} /> : <span className="aq-muted">—</span>
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('tutorVerdict', row => (
      <CorrectnessBadge value={row.correctness} />
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('studentAccuracy', row => (
      <ScoreCell value={row.correctnessScore} />
    ), { sortable: true, extraClass: 'aq-num' }),
    sourceColumn<StudentAnalysisRow>('studentApproach', row => (
      <ScoreCell value={row.approachScore} />
    ), { sortable: true, extraClass: 'aq-num' }),
    sourceColumn<StudentAnalysisRow>('assessmentQuality', row => (
      <AssessmentQualityCell facts={row} />
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('config', row => (
      <div className="aq-attempt">
        <span>{row.config ? configLabel(row.config) : '—'}</span>
        {row.model && <span className="aq-muted" title={row.provider}>{row.model}</span>}
      </div>
    ), { sortable: true }),
    sourceColumn<StudentAnalysisRow>('health', row => <HealthCell facts={row} />, { sortable: true }),
  ], []);

  // The URL param seeded the filter; once the user changes it, the URL should
  // stop claiming otherwise.
  const onFilterChange = (next: Record<string, string>) => {
    setFilters(next);
    setPage(1);
    if (arrivedFrom && next.assignment !== arrivedFrom) {
      const url = new URLSearchParams(params);
      url.delete('questionSetId');
      setParams(url, { replace: true });
    }
  };

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <div className="aq-page">
      <div className="aq-breadcrumb">
        <Link to="/evals?tab=assignments">Assignments</Link> / <span>{data?.student.name ?? studentId}</span>
      </div>

      <div className="aq-head">
        <h2>{data?.student.name ?? studentId}</h2>
        <p className="aq-sub">
          Every solution analysis for this student — assignment work, practice, everything.
        </p>
      </div>



      <div className="aq-filters">
        <span className="aq-muted">{rows.length} analyses loaded from #{offset + 1}</span>
        <button className="btn btn-secondary" disabled={offset === 0}
          onClick={() => { setOffset(Math.max(0, offset - windowSize)); setPage(1); }}>
          Newer
        </button>
        <button className="btn btn-secondary" disabled={!data?.hasMore}
          onClick={() => { setOffset(offset + windowSize); setPage(1); }}>
          Older
        </button>
      </div>

      <DataTable
        toolbar={
          <SearchFilterBar
          filters={filterDefs}
          values={filters}
          onChange={onFilterChange}
          searchPlaceholder="question, answer, assignment"
          // The window refetches and everything else filters in memory, so there
          // is nothing for a Fetch button to commit.
          hideFetch
          />
        }
        columns={columns}
        columnGroups={sourceGroupRow([
          ['attempt', 2], ['question', 2], ['attempt', 1], ['tutor', 2],
          ['scorer', 2], ['judge', 1], ['attempt', 1], ['checks', 1],
        ])}
        data={pageRows}
        rowKey={row => row.submissionId}
        loading={isLoading}
        emptyMessage="No analyses match these filters."
        sort={sort}
        onSortChange={next => { setSort(next); setPage(1); }}
        pagination={{
          page: safePage,
          totalPages,
          total: sorted.length,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
